// Keeps the ember scene inside its frame budget on whatever device runs it.
// Fed one frame interval at a time, it answers with an action when recent
// frames say the scene should get cheaper (or can afford to get sharper):
// first the render scale, then a lighter tier, then the still poster.
//
// Frame intervals measure the browser's pace as well as the scene's cost.
// Browsers saving power cap requestAnimationFrame at 30 Hz (iOS Low Power
// Mode, Chrome's Energy Saver), and those steady 33 ms frames look exactly like
// a GPU that is too slow. So before acting on frames near 30 fps the governor
// asks the loop to measure the pace (a few callbacks with nothing drawn arrive
// as fast as the browser allows) and judges against that instead.
//
// Pure: the controller applies the actions, the tests drive it directly.

import { TIER_ORDER } from './config'
import type { Tier, TierName } from './config'

export type GovernorAction =
  | { kind: 'scale'; scale: number }
  | { kind: 'tier'; tier: TierName }
  | { kind: 'pace' }
  | { kind: 'poster' }

export interface GovernorOptions {
  /** Frames per verdict... */
  window?: number
  /** ...or a verdict this often, given a few frames. */
  windowMs?: number
  /** Frames ignored after a change, or this long, whichever ends first. */
  settle?: number
  settleMs?: number
}

const HITCH_MS = 250 // one frame this long is a stall (tab switch, GC), not load
const SLOW_STREAK = 5 // ...but this many in a row is the device
const MIN_SAMPLES = 4
const CAPPED_MIN_MS = 28 // windows a 30 Hz browser cap could explain
const CAPPED_MAX_MS = 40
const PACE_EVERY_MS = 20000
const CAP_TOLERANCE = 1.3 // capped frames land on display ticks: 40 ms at 75 Hz
const STEADY_SPREAD = 1.5 // slow tenth within 1.5x the fastest frame keeps the display's pace
const TRIAL_MS = 4000 // a buy-back that overruns this soon is undone...
const COOLDOWN_MS = 30000 // ...and the next one waits this long

function percentile(sorted: number[], p: number): number {
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))]
}

export class Governor {
  tier: Tier
  scale: number
  /** The browser's own frame interval from the last measurement; 0 when unknown. Kept across tiers. */
  paceMs = 0

  private samples: number[] = []
  private windowStart = -1
  private settleLeft: number
  private settleUntil = Number.NaN
  private calm = 0
  private slow = 0
  private capMs = 0
  private pacedAt = Number.NEGATIVE_INFINITY
  private trialFrom = 1
  private trialUntil = Number.NEGATIVE_INFINITY
  private buyBackAfter = Number.NEGATIVE_INFINITY
  private readonly window: number
  private readonly windowMs: number
  private readonly settleFrames: number
  private readonly settleMs: number

  constructor(tier: Tier, options: GovernorOptions = {}) {
    this.tier = tier
    this.scale = tier.maxScale
    this.window = options.window ?? 45
    this.windowMs = options.windowMs ?? 1500
    this.settleFrames = options.settle ?? 30
    this.settleMs = options.settleMs ?? 700
    this.settleLeft = this.settleFrames
  }

  reset(tier: Tier, scale = tier.maxScale): void {
    this.tier = tier
    this.scale = scale
    this.calm = 0
    this.slow = 0
    this.capMs = 0
    this.trialUntil = Number.NEGATIVE_INFINITY
    this.buyBackAfter = Number.NEGATIVE_INFINITY
    this.restart(Number.NaN)
  }

  /** The answer to a `pace` action: the shortest interval between callbacks that drew nothing. */
  pace(ms: number, now = performance.now()): void {
    this.pacedAt = now
    this.paceMs = ms > 0 && Number.isFinite(ms) ? Math.min(ms, CAPPED_MAX_MS) : 0
  }

  /**
   * One interval between drawn frames. `capMs` is the interval the loop held
   * frames to on purpose (the low tier, an idle page), which no frame can beat.
   */
  sample(ms: number, now = performance.now(), capMs = 0): GovernorAction | null {
    if (!(ms > 0)) return null
    if (capMs !== this.capMs) {
      this.capMs = capMs
      this.samples = []
      this.windowStart = -1
    }

    if (ms > HITCH_MS) {
      if (++this.slow < SLOW_STREAK) return null
      this.slow = 0
      this.restart(now)
      return this.lighter(true)
    }
    this.slow = 0

    if (this.settleLeft > 0) {
      if (Number.isNaN(this.settleUntil)) this.settleUntil = now + this.settleMs
      if (now < this.settleUntil) {
        this.settleLeft--
        return null
      }
      this.settleLeft = 0
    }

    if (this.windowStart < 0) this.windowStart = now
    this.samples.push(ms)
    const full = this.samples.length >= this.window
    const timely = now - this.windowStart >= this.windowMs && this.samples.length >= MIN_SAMPLES
    if (!full && !timely) return null

    const sorted = [...this.samples].sort((a, b) => a - b)
    this.samples = []
    this.windowStart = -1
    const p25 = percentile(sorted, 0.25)
    const p75 = percentile(sorted, 0.75)
    const p90 = percentile(sorted, 0.9)

    // A cap delivers no frame early: a quarter well inside it means the browser lifted it.
    if (this.paceMs > this.tier.targetMs && p25 < this.paceMs * 0.75) this.paceMs = 0
    const budget = Math.max(this.tier.targetMs, this.capMs, this.paceMs)
    const over = Math.max(budget * 1.18, this.capMs * CAP_TOLERANCE)

    if (p75 > over) {
      this.calm = 0
      if (now <= this.trialUntil) {
        // The last buy-back did not hold: undo it and wait before trying again.
        this.trialUntil = Number.NEGATIVE_INFINITY
        this.buyBackAfter = now + COOLDOWN_MS
        this.scale = this.trialFrom
        this.restart(now)
        return { kind: 'scale', scale: this.scale }
      }
      if (p75 >= CAPPED_MIN_MS && p75 <= CAPPED_MAX_MS && now - this.pacedAt >= PACE_EVERY_MS) {
        this.pacedAt = now
        return { kind: 'pace' }
      }
      const atFloor = this.scale <= this.tier.minScale + 0.01
      if (p75 > budget * 3) {
        this.restart(now)
        if (atFloor) return this.lighter(false)
        this.scale = this.tier.minScale
        return { kind: 'scale', scale: this.scale }
      }
      const next = Math.max(this.tier.minScale, this.scale * (p75 > budget * 1.8 ? 0.78 : 0.88))
      if (next < this.scale - 0.01) {
        this.scale = next
        this.restart(now)
        return { kind: 'scale', scale: next }
      }
      if (TIER_ORDER.indexOf(this.tier.name) < TIER_ORDER.length - 1) {
        this.restart(now)
        return this.lighter(false)
      }
      // The lightest tier at its lowest scale still cannot hold about 20 fps.
      return p75 > 50 ? { kind: 'poster' } : null
    }

    const steady = p90 < budget * 0.62 || p90 <= sorted[0] * STEADY_SPREAD
    if (steady && this.scale < this.tier.maxScale - 0.01 && now >= this.buyBackAfter) {
      if (++this.calm >= 3) {
        this.calm = 0
        this.trialFrom = this.scale
        this.scale = Math.min(this.tier.maxScale, this.scale * 1.08)
        this.trialUntil = now + TRIAL_MS
        this.restart(now)
        return { kind: 'scale', scale: this.scale }
      }
    } else {
      this.calm = 0
    }
    return null
  }

  private restart(now: number): void {
    this.samples = []
    this.windowStart = -1
    this.settleLeft = this.settleFrames
    this.settleUntil = Number.isNaN(now) ? Number.NaN : now + this.settleMs
  }

  private lighter(toLowest: boolean): GovernorAction {
    const index = TIER_ORDER.indexOf(this.tier.name)
    if (index >= TIER_ORDER.length - 1) return { kind: 'poster' }
    return { kind: 'tier', tier: toLowest ? TIER_ORDER[TIER_ORDER.length - 1] : TIER_ORDER[index + 1] }
  }
}
